import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Quote, Plus } from "lucide-react";
import { FadeUp, Overline, MaskedLines } from "../components/Reveal";
import Seo from "../components/Seo";
import { IMAGES, LINKS, BOOK_LESSONS } from "../data/content";
import { LESSONS_50 } from "../data/lessons";

const FORMATS = ["Kindle", "Paperback", "Hardcover", "Audiobook"];

const FiftyLessons = () => {
  const [open, setOpen] = useState(null);
  return (
    <section data-testid="book-fifty-lessons" className="border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-32">
        <FadeUp>
          <Overline>The Bonus Section</Overline>
          <h2 className="font-display font-medium tracking-tighter leading-[0.95] text-4xl sm:text-5xl text-paper mt-6 max-w-2xl">
            Fifty lessons readers call the most useful part of the book
          </h2>
          <p className="mt-6 text-white/50 text-base font-body font-light max-w-xl">
            Each lesson pairs a hard-earned truth with a reflection that does not let you off the hook. Tap any lesson to read it.
          </p>
        </FadeUp>
        <div className="mt-16">
          {LESSONS_50.map((lesson) => (
            <FadeUp key={lesson.n} delay={0.01}>
              <div className="border-t border-white/10 last:border-b" data-testid={`lesson-row-${lesson.n}`}>
                <button
                  onClick={() => setOpen(open === lesson.n ? null : lesson.n)}
                  data-testid={`lesson-toggle-${lesson.n}`}
                  className="group w-full flex items-baseline gap-6 md:gap-10 py-6 text-left"
                >
                  <span className="font-display font-light text-2xl md:text-3xl text-stroke leading-none shrink-0 w-12">
                    {String(lesson.n).padStart(2, "0")}
                  </span>
                  <span className={`font-display font-light tracking-tight text-xl md:text-2xl leading-snug flex-1 transition-colors duration-300 ${open === lesson.n ? "text-brand" : "text-paper group-hover:text-brand"}`}>
                    {lesson.title}
                  </span>
                  <Plus
                    size={18}
                    className={`shrink-0 text-white/40 transition-transform duration-300 ${open === lesson.n ? "rotate-45 text-brand" : "group-hover:text-brand"}`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {open === lesson.n && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="pb-8 pl-[4.5rem] md:pl-[5.5rem] pr-6 max-w-3xl">
                        <p className="text-white/55 text-base leading-relaxed font-body font-light" data-testid={`lesson-body-${lesson.n}`}>
                          {lesson.body}
                        </p>
                        <p className="mt-4 text-brand/90 text-sm italic font-body font-light border-l-2 border-brand pl-4" data-testid={`lesson-question-${lesson.n}`}>
                          {lesson.question}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function Book() {
  return (
    <main data-testid="book-page" className="pt-40">
      <Seo
        title="The Franchise Mindset — Book by Gurpreet Paul"
        description="The Franchise Mindset: How Vision, Persistence, and Systems Built a Modern Beverage Brand. The 2026 book by Gurpreet Paul with 50 business lessons. Available on Amazon in Kindle, paperback, hardcover and audiobook."
      />
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-32 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <FadeUp>
            <Overline>The Book · Published June 2026</Overline>
          </FadeUp>
          <MaskedLines
            lines={["The Franchise", "Mindset"]}
            className="font-display font-medium tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl text-paper mt-6"
            delay={0.15}
          />
          <FadeUp delay={0.35}>
            <p className="mt-4 text-[13px] uppercase tracking-[0.2em] text-brand font-body">
              How Vision, Persistence, and Systems Built a Modern Beverage Brand
            </p>
            <p className="mt-8 text-white/55 text-base md:text-lg leading-relaxed font-body font-light max-w-xl">
              There is a version of the entrepreneurial journey that gets shared on social media — the clean wins,
              the milestone photos. Then there is the real version. The Franchise Mindset is that real version:
              the story of building ThirsTea USA from a blank page into a premium franchise brand, with a complete
              framework any entrepreneur, franchise buyer or small business owner can apply directly.
            </p>
            <p className="mt-5 text-white/55 text-base leading-relaxed font-body font-light max-w-xl">
              Every chapter opens with a personal story and closes with a business framework, reflection questions
              and five clear takeaways — ending with a bonus section of fifty business lessons readers call the
              most useful part of all.
            </p>
            <div className="mt-8 flex flex-wrap gap-2" data-testid="book-formats">
              {FORMATS.map((f) => (
                <span key={f} className="rounded-full border border-white/15 px-4 py-1.5 text-[11px] uppercase tracking-[0.12em] text-white/50 font-body">
                  {f}
                </span>
              ))}
            </div>
            <a
              href={LINKS.amazon}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="book-amazon-cta"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-brand px-8 py-4 text-[12px] uppercase tracking-[0.15em] text-white hover:bg-brand-dark transition-colors duration-300"
            >
              Get it on Amazon <ArrowUpRight size={15} />
            </a>
          </FadeUp>
        </div>

        <div className="lg:col-span-6 order-1 lg:order-2 relative flex justify-center" data-testid="book-cover-wrap">
          <div className="absolute w-[420px] h-[420px] rounded-full bg-brand/20 blur-[130px] pointer-events-none" />
          <motion.div
            animate={{ y: [-10, 10, -10] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            whileHover={{ rotate: -2, scale: 1.02 }}
            className="relative"
          >
            <motion.img
              src={IMAGES.bookCover}
              alt="The Franchise Mindset — book cover by Gurpreet Paul"
              data-testid="book-cover-image"
              initial={{ opacity: 0, y: 40, rotate: 4 }}
              animate={{ opacity: 1, y: 0, rotate: 0 }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-72 md:w-96 red-glow border border-white/10"
            />
          </motion.div>
        </div>
      </section>

      <section data-testid="book-lessons" className="border-t border-white/10 bg-[#0D0D0F]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-32">
          <FadeUp>
            <Overline>From the Pages</Overline>
            <h2 className="font-display font-medium tracking-tighter leading-[0.95] text-4xl sm:text-5xl text-paper mt-6 max-w-2xl">
              Operational truths, earned under real pressure
            </h2>
          </FadeUp>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {BOOK_LESSONS.map((lesson, i) => (
              <FadeUp key={i} delay={i * 0.08}>
                <div
                  data-testid={`book-lesson-${i + 1}`}
                  className="group bg-[#0A0A0A] p-10 md:p-12 h-full hover:bg-[#141416] transition-colors duration-500"
                >
                  <Quote size={20} className="text-brand mb-6" />
                  <p className="font-display font-light text-2xl md:text-3xl leading-snug text-paper group-hover:text-brand transition-colors duration-500">
                    {lesson}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <FiftyLessons />

      <section data-testid="book-author-note" className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <FadeUp className="lg:col-span-5">
            <div className="overflow-hidden border border-white/10">
              <img
                src={IMAGES.portraitGrey}
                alt="Gurpreet Paul — author"
                loading="lazy"
                data-testid="book-author-image"
                className="w-full h-[420px] object-cover object-top hover:scale-[1.03] transition-transform duration-[1.2s]"
              />
            </div>
          </FadeUp>
          <FadeUp delay={0.15} className="lg:col-span-7">
            <Overline>Why He Wrote It</Overline>
            <blockquote className="mt-6 font-display font-light tracking-tight text-3xl md:text-4xl leading-[1.2] text-paper">
              "The right knowledge, shared honestly by someone who has genuinely lived it,
              <span className="text-brand italic"> changes what is possible</span> for the person receiving it."
            </blockquote>
            <p className="mt-8 text-white/50 text-base leading-relaxed font-body font-light max-w-xl">
              Written the way the best mentors speak — directly, warmly, and without the polish that usually hides
              the truth. Not the version of the story designed to impress you. The version designed to genuinely help you.
            </p>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
